import React, {Fragment, memo, MouseEvent, useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import ReactModal from 'react-modal/lib/components/Modal';
import ImageGallery from 'react-image-gallery';

import {translate} from '@mapsight/ui/src/js/helpers/i18n';

import {detailsFeatureSelector} from '../../mapsight-glue/store/selectors';
import {useNavPos} from '../../hooks';
import {isMainEvent} from '../../modules/helpers';

import Image from './image';


export default memo(
	function DetailsImpressions({ }) {
		const detailsFeature = useSelector(detailsFeatureSelector);
		const [galleryStartIndex, setGalleryStartIndex] = useState(
			/** @type {null | number} */(null)
		);

		const onCloseGallery = useCallback(
			() => {
				setGalleryStartIndex(null);
			},
			[],
		);

		if (detailsFeature === null) {
			return null;
		}

		// TODO Stade: prüfen, ob überhaupt Bilder vorliegen
		// leider hat impressionsSubHeading einen Typo beim großen H. aber der bleibt aus Budgetgründen (subheading ist ein Wort im Englischen)
		return (
			<Fragment>
				<div className="stade-details-impressions ms3-map-row ms3-flex ms3-flex--row">
					<header>
						<h2>{translate('stade.impressions.heading')}</h2>
						<p>{detailsFeature?.properties?.impressionsSubHeading || detailsFeature?.properties?.subheading || translate('stade.impressions.subheading')}</p>
					</header>
				</div>
				<div className="stade-details-impressions__images ms3-flex--row">
					{detailsFeature.properties.images.map((img, i) => (
						<DetailsImpressionsImage
							key={i}
							image={img}
							index={i}
							onActivateGallery={setGalleryStartIndex}
						/>
					))}
				</div>

				{galleryStartIndex !== null && (
					<ReactModal
						isOpen={galleryStartIndex !== null}
						onRequestClose={onCloseGallery}
						className="stade-details-impressions__gallery"
						overlayClassName="stade-details-impressions__gallery__overlay"
						shouldCloseOnOverlayClick={false}
						>
						<DetailsImpressionsGallery
							startIndex={galleryStartIndex}
							images={detailsFeature.properties.images}
							onClose={onCloseGallery}
						/>
					</ReactModal>
				)}
			</Fragment>
		);
	}
);

const DetailsImpressionsImage = memo(
	/**
	 * @param {{
	 *   image: import('../types').Image,
	 *   index: number,
	 *   onActivateGallery: (index: number) => void,
	 * }} props
	 */
	function DetailsImpressionsImage({ image, index, onActivateGallery }) {
		const onClick = useCallback(
			/** @param {MouseEvent<HTMLAnchorElement>} event */
			(event) => {
				if (isMainEvent(event)) {
					event.preventDefault();
					onActivateGallery(index);
				}
			},
			[onActivateGallery, index],
		);

		return (
			<div className="stade-details-impressions__image" onClick={onClick}>
				<Image
					className="stade-details-impressions__image__image"
					image={image}
					widthPercent={20}
				/>

				{image.caption.length !== 0 && (
					<div className="stade-details-impressions__image-caption">
						{image.caption}
					</div>
				)}
			</div>
		);
	}
);

const DetailsImpressionsGallery = memo(
	/** @param {{
	 *   images: Array<import('../types').Image>,
	 *   startIndex: number,
	 *   onClose: () => void,
	 * }} props */
	function DetailsImpressionsGallery({ images, startIndex, onClose }) {
		const galleryItems = useMemo(
			() => images.map((img, index) => {
				return /** @type {GalleryItem}*/ ({
					original: img.url,
					image: img,
					index,
					totalImageNumber: images.length,
				});
			}),
			[images],
		);

		useEffect(
			() => {
				/** @param {KeyboardEvent} e */
				function onKeyDown(e) {
					if (isMainEvent(e) && e.key === "Escape") {
						onClose();
						e.preventDefault();
					}
				}
				window.document.addEventListener("keydown", onKeyDown);
				return () => {
					window.document.removeEventListener("keydown", onKeyDown);
				};
			},
			[onClose],
		);

		const renderGalleryItem = useCallback(
			/**
			 * @param {GalleryItem} item
			 */
			(item) => (
				<GalleryItem item={item} onClose={onClose} />
			),
			[onClose],
		);

		return (
			// <div className="stade-details-impressions__gallery">
			<Fragment>
				<div
					className="stade-details-impressions__gallery__background"
					onClick={onClose}
				/>

				<div className="stade-details-impressions__gallery__gallery">
					<ImageGallery
						items={galleryItems}
						startIndex={startIndex}
						showThumbnails={false}
						showPlayButton={false}
						showFullscreenButton={false}
						slideDuration={0}
						// @ts-ignore
						renderItem={renderGalleryItem}
					/>
				</div>
			</Fragment>
			// </div>
		);
	}
);

/**
 * @param {{
 *   item: GalleryItem,
 *   onClose: () => void,
 * }} props
 */
function GalleryItem({ item, onClose }) {
	const navPos = useNavPos();

	return (
		<div className="stade-details-impressions__gallery-item">
			<div className="stade-details-impressions__gallery-item__top-bar">
				<button
					type="button"
					className="stade-details-impressions__gallery-item__close-button"
					onClick={onClose}
				>
					{"×"}
				</button>
			</div>

			<Image
				className="stade-details-impressions__gallery-item__image"
				image={item.image}
				widthPercent={100}
			/>

			<div className="stade-details-impressions__gallery-item__bottom-bar">
				{item.image.caption.length !== 0 && (
					<div className="stade-details-impressions__gallery-item__caption">
						{item.image.caption}
					</div>
				)}

				<div className="stade-details-impressions__gallery-item__image-number">
					{item.index + 1}
					{navPos.app?.lang === 'de' ? ' von ' : ' of '}
					{item.totalImageNumber}
				</div>
			</div>
		</div>
	);
}

/** @typedef {import('react-image-gallery').ReactImageGalleryItem & {
 *   image: import('../types').Image,
 *   index: number,
 *   totalImageNumber: number,
 * }} GalleryItem */
